import React from 'react';
import {FormField, Game as GameType} from "../../types/games";
import {Language, translations} from "../../translations";
import {useAppDispatch} from "../../app/store";
import {setFormData} from "../../app/slices/game";
import defaultStart from "../../images/welcome.jpg";
import {CSSTransition} from 'react-transition-group';
import {TRANSITION_DURATION} from './Game';
import toast from 'react-hot-toast';
import FormFields from "./FormFields";

type Props = {
    handleStart: () => void;
    gameData: GameType;
    slug: string | undefined;
    lang: Language;
}

const GameStart = ({handleStart, gameData, slug, lang}: Props) => {
    const dispatch = useAppDispatch();
    const [isVisible, setIsVisible] = React.useState(true);
    const transRef = React.useRef(null);
    const LANG = translations[lang];

    React.useEffect(() => {
        setIsVisible(true);
    }, []);


    let languageOptions;
    if (gameData.languages.length > 0) {
        languageOptions = gameData.languages.map((lang, i) => {
            const selectedClass = lang.game.slug === slug ? 'selected-language' : '';
            return (
                <a className={selectedClass} key={i} href={lang.game.slug}>{lang.name}</a>
            );
        });
    }

    const isValid = gameData.form.every((field: FormField) => !field.required || (field.required && field.value !== ''));

    const handleGameStart = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) {
            toast.error(LANG.fill_all_fields, {position: 'top-right',});
            return;
        }
        setIsVisible(false);
    }


    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // 'keypress' event misbehaves on mobile so we track 'Enter' key via 'keydown' event
        if (event.key === 'Enter' && isValid) {
            event.preventDefault();
            event.stopPropagation();
            handleGameStart(event);
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        dispatch(setFormData({[name]: value}));
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = e.target;
        dispatch(setFormData({[name]: checked ? '1' : ''}));
    }

    const nextBtn = isValid
        ? (
            <div className="circle-button pulse active" onClick={handleGameStart}>
                <div className="triangle-right"></div>
            </div>
        )
        : (
            <div className="circle-button">
                <div className="triangle-right"></div>
            </div>
        );

    const gameImage = gameData.image ? gameData.image : defaultStart;

    return (
        <div>
            <CSSTransition nodeRef={transRef}
                           onEnter={() => setIsVisible(true)}
                           onExited={() => handleStart()}
                           in={isVisible}
                           unmountOnExit
                           timeout={TRANSITION_DURATION}
                           classNames="slide-right">
                <div className='game-start-screen' ref={transRef}>
                    <div className='game-card-header game-card-header-start pb-1'>
                        <div className='container-fluid pb-2'>
                            {languageOptions && languageOptions.length > 1 &&
                                <div className='game-card-header-top pt-1 pb-2 pb-md-0'>
                                    {languageOptions}
                                </div>
                            }
                            <div className='row text-center text-md-start'>
                                <div className='col-12 col-md-3 pe-md-0 pt-3'>
                                    <img src={gameImage} alt={gameData.name}
                                         className='game-thumbnail img-fluid'/>
                                </div>
                                <div className='col-12 col-md-9 d-flex px-md-4 pt-3'>
                                    <div className='m-auto'>
                                        <h1>{gameData.name}</h1>
                                        <div dangerouslySetInnerHTML={{__html: gameData.description}}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {gameData.form && gameData.form.length > 0 &&
                        <div className='game-card-body'>
                            <div className='container'>
                                <div className='row'>
                                    <div className='col-12 col-md-8 offset-md-2 pb-3'>
                                        <form onSubmit={handleGameStart} id='game-start'
                                              className='row pb-4 gy-2'
                                              action='' method='POST'>
                                            <FormFields form={gameData.form}
                                                        handleCheckboxChange={handleCheckboxChange}
                                                        handleInputChange={handleInputChange}
                                                        onKeyDown={onKeyDown} />
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    <div className='glowing-bar'></div>
                </div>
            </CSSTransition>
            <div className='game-bottom-bar'>
                {nextBtn}
            </div>
        </div>
    );
};

export default GameStart;